import { Button, Popover, Stack, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavbarSelector } from '../../../state/store';
import { selectors } from '../../../state/state';
import { navbarTestIds } from '../../../testids';
import classes from './ObservationModePill.module.css';

/**
 * Observation Mode indicator — the navbar pill (ADR-068).
 *
 * ## What it says
 *
 * The account is in Observation Mode: Teselly ingests every inbound fact and runs
 * every rule, but suppresses tenant-behalf OUTBOUND effects (channel writes,
 * retail invoicing, buyer messages), recording what it *would* have done as
 * Desired Channel State.
 *
 * The copy deliberately does NOT say "solo lectura" / "read-only". The account is
 * not read-only — data keeps flowing in — and `clients-management/CONTEXT.md`
 * lists "Read-only account" as a term to avoid for exactly this reason. A user
 * told their account is read-only reasonably concludes their orders stopped
 * importing, which is false and is a support ticket.
 *
 * ## Why a pill and not a banner
 *
 * Observation is the DEFAULT for a fresh trial and can last weeks. A full-width
 * strip is the visual language of an incident; it works on day one and is furniture
 * by day ten. A pill states a normal condition at the weight of a normal condition,
 * costs no vertical space, and — the load-bearing reason — is the only shape with
 * somewhere to put the per-connector-kind overrides the domain already models
 * (`connectorKindEgressModes`) when those become tenant-facing. Nothing here has to
 * be redesigned for that; the popover just grows a section.
 *
 * ## The disabled action
 *
 * `GoLive` is admin/system-only today — the aggregate declares `.withSubdomain('')`
 * — so no tenant can flip themselves live yet. The button is rendered anyway, and
 * disabled, because it is the whole point of the surface: the user should learn
 * now that this is a mode they will own, not a restriction imposed on them. The
 * hint below it says why it is unavailable, so a disabled control is never a dead
 * end without an explanation. When GoLive opens up, this button gets an `onClick`
 * and a confirmation — nothing moves.
 *
 * ## Where the state comes from
 *
 * `state.account.data.observationStatus`, already carried by the existing
 * `accountSubscriptionPolicy` subscription to `AccountsReadmodel`. No extra query,
 * no second policy, and it updates live when the projection lands.
 *
 * Renders `null` for a live account, which is the overwhelming majority: the field
 * is never seeded, so an account that never entered observation reads `undefined`
 * and the domain resolves that to `live`. Zero DOM impact for them.
 */
export function ObservationModePill() {
  const { t } = useTranslation( 'layout' );
  const isObserving = useNavbarSelector( selectors.isObserving );

  if ( !isObserving ) return null;

  return (
    <Popover
      width={296}
      position="bottom-end"
      withArrow
      arrowSize={10}
      shadow="md"
      radius="md"
      trapFocus
    >
      <Popover.Target>
        <UnstyledButton
          className={classes.pill}
          aria-label={t( 'observation_pill_aria_label', 'Modo observación — ver detalle' )}
          data-testid={navbarTestIds.observationPill}
        >
          <span className={classes.dot} aria-hidden="true" />
          <span className={classes.label} data-testid={navbarTestIds.observationPillLabel}>
            {t( 'observation_pill_label', 'Modo observación' )}
          </span>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown data-testid={navbarTestIds.observationPopover}>
        <Stack gap="sm">
          <Text fz="sm" fw={600}>
            {t( 'observation_popover_title', 'Modo observación' )}
          </Text>

          <Text fz="xs" c="dimmed" lh={1.5}>
            {t(
              'observation_popover_body',
              'Importamos tus órdenes y publicaciones, y calculamos cada cambio que corresponde. Todavía no los enviamos a tus canales.'
            )}
          </Text>

          <Button
            size="sm"
            radius="xs"
            fullWidth
            disabled
            styles={{ label: { fontSize: '12px' }}}
            data-testid={navbarTestIds.observationGoLiveButton}
          >
            {t( 'observation_go_live_action', 'Activar modo en vivo' )}
          </Button>

          <Text
            fz="xs"
            c="dimmed"
            ta="center"
            data-testid={navbarTestIds.observationGoLiveHint}
          >
            {t(
              'observation_go_live_hint',
              'Te avisamos cuando puedas activarlo vos mismo.'
            )}
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

export default ObservationModePill;
